// KYRS pick -> METIS row. When a task is scheduled for TODAY in KYRS, make
// sure an agenda_items row exists for it (linked by task_id) so METIS shows it
// as an editable row. Idempotent per (user, date, task_id). Unpicking never
// deletes: once a row exists the user may have touched it.
import { supabase } from '../../lib/supabase';

const listeners = new Set<() => void>();
/** METIS subscribes to refetch today's rows after a KYRS pick inserts one. */
export function subscribeAgendaRowsChanged(fn: () => void): () => void {
  listeners.add(fn);
  return () => { removeListener(fn); };
}
function removeListener(fn: () => void) { listeners.delete(fn); }

const pad2 = (n: number) => (n < 10 ? `0${n}` : `${n}`);
export function todayKey(d = new Date()): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
const genId = () => Math.random().toString(36).substring(2, 9);

/** 'YYYY-MM-DD' or 'YYYY-MM-DD HH:MM' -> { date, time:'h:mm AM' | '' } */
function splitScheduled(s: string): { date: string; time: string } | null {
  const m = /^(\d{4}-\d{2}-\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(s.trim());
  if (!m) return null;
  let time = '';
  if (m[2]) {
    const h = parseInt(m[2], 10);
    time = `${h % 12 === 0 ? 12 : h % 12}:${m[3]} ${h >= 12 ? 'PM' : 'AM'}`;
  }
  return { date: m[1], time };
}

/** Call after a task's `scheduled` was written. No-op unless it is today. */
export async function ensureAgendaRowForPickedTask(
  userId: string,
  taskId: string,
  scheduled: string | null | undefined,
): Promise<{ created: boolean; error?: string }> {
  try {
    if (!scheduled) return { created: false };
    const parts = splitScheduled(scheduled);
    const date = todayKey();
    if (!parts || parts.date !== date) return { created: false };

    const { data: existing, error: exErr } = await supabase
      .from('agenda_items')
      .select('id')
      .eq('user_id', userId)
      .eq('date', date)
      .eq('task_id', taskId)
      .limit(1);
    if (exErr) return { created: false, error: exErr.message };
    if (existing && existing.length > 0) return { created: false };

    const { data: task, error: tErr } = await supabase
      .from('tasks')
      .select('title, sector, design_id, aim_ref')
      .eq('id', taskId)
      .eq('user_id', userId)
      .single();
    if (tErr || !task) return { created: false, error: tErr?.message ?? 'task not found' };

    const { data: last } = await supabase
      .from('agenda_items')
      .select('sort_order')
      .eq('user_id', userId)
      .eq('date', date)
      .order('sort_order', { ascending: false })
      .limit(1);
    const nextOrder = last && last.length > 0 ? ((last[0] as any).sort_order ?? 0) + 1 : 0;

    const { error: insErr } = await supabase.from('agenda_items').insert({
      user_id: userId,
      date,
      item_id: genId(),
      completed: false,
      text: (task as any).title ?? '',
      time: parts.time,
      duration: '',
      sort_order: nextOrder,
      nails: [],
      design: null,
      task_id: taskId,
      sector: (task as any).sector ?? null,
      design_id: (task as any).design_id ?? null,
      aim_ref: (task as any).aim_ref ?? null,
      updated_at: new Date().toISOString(),
    });
    if (insErr) return { created: false, error: insErr.message };
    listeners.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
    return { created: true };
  } catch (e: any) {
    return { created: false, error: e?.message ?? String(e) };
  }
}
